import Grid from '@mui/material/Grid';
import { useState } from 'react';

import HighlightLightboxImagePlayer from '@/components/HighlightLightboxPlayer/HighlightLightboxImagePlayer';
import GridContainer from '@/components/Layout/GridContainer/GridContainer';
import RichText from '@/components/RichText/RichText';
import type { FrontifyImageContent } from '@/contentful/contentful-exports';
import { useInteraction } from '@/hooks/useInteraction';
import useViewPortSize from '@/hooks/useViewportSize';
import useGlobalStyles from '@/styles/global.styles';
import type { EmbeddedImageCf } from '@/types';
import { setImgWidth, setSrcSetImg } from '@/utils/frontify';

import useStyles from './EmbeddedImage.styles';

export default function EmbeddedImage({
  content,
}: {
  content: EmbeddedImageCf;
}) {
  const { classes, cx } = useStyles();
  const { classes: globalClasses } = useGlobalStyles({ fixed: true });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { isMobile } = useViewPortSize();
  const { captionText, image, altText, modal } = content.fields;
  const [defaultImage, mobileImage] =
    image as unknown as FrontifyImageContent[];
  const lightBoxImage = mobileImage && isMobile ? mobileImage : defaultImage;
  const imageSize = isMobile ? 768 : 1440;

  const handleInteraction = useInteraction(() => {
    if (modal) setIsModalOpen(true);
  });

  if (!defaultImage) return null;

  return (
    <GridContainer fixed gridProps={{ className: classes.grid }}>
      <Grid size={{ md: 10 }}>
        {/** biome-ignore lint/a11y/useSemanticElements: Automated migration to Biome */}
        <div
          role='button'
          tabIndex={0}
          onClick={handleInteraction}
          onKeyDown={handleInteraction} // Unified handling
          aria-label={altText || 'Embedded Image'}
        >
          {/** biome-ignore lint/performance/noImgElement: Automated migration to Biome */}
          <img
            src={setImgWidth(defaultImage.preview_url, imageSize)}
            srcSet={setSrcSetImg(defaultImage.preview_url, imageSize)}
            alt={altText}
            data-testid='default-image'
            className={cx(classes.image, {
              [classes.hiddenOnMobile]: !!mobileImage,
            })}
          />
        </div>
        {mobileImage && (
          // biome-ignore lint/a11y/useSemanticElements: Automated migration to Biome
          <div
            role='button'
            tabIndex={0}
            onClick={handleInteraction}
            onKeyDown={handleInteraction} // Unified handling
            aria-label={altText || 'Mobile Embedded Image'}
          >
            {/** biome-ignore lint/performance/noImgElement: Automated migration to Biome */}
            <img
              src={setImgWidth(mobileImage.preview_url, imageSize)}
              srcSet={setSrcSetImg(mobileImage.preview_url, imageSize)}
              alt={altText}
              data-testid='mobile-image'
              className={cx(classes.image, classes.mobileImage)}
            />
          </div>
        )}
        {captionText && (
          <Grid>
            <RichText
              content={captionText}
              className={cx(globalClasses.bodySm, classes.caption)}
            />
          </Grid>
        )}
      </Grid>

      <HighlightLightboxImagePlayer
        image={lightBoxImage}
        altText={altText || 'Enlarged Embedded Image'}
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      />
    </GridContainer>
  );
}
